import { BookingSlot } from '../../api-types/models/Booking';
import { addDays, addHours, addMinutes } from 'date-fns';
import { findSlotsForTimeFrame } from './findSlotsForTimeFrame';

function mergeOccupiedIntervals(
    intervals: { start: number; end: number }[]
): { start: number; end: number }[] {
    const sorted = [...intervals].sort((a, b) => a.start - b.start);
    const merged: { start: number; end: number }[] = [];

    for (const interval of sorted) {
        const last = merged[merged.length - 1];
        if (last && interval.start <= last.end) {
            last.end = Math.max(last.end, interval.end);
        } else {
            merged.push({ ...interval });
        }
    }

    return merged;
}

/** Start time for a new slot when the user clicks empty space in a calendar cell. */
export function getAvailableFrameStartFromCellClick(
    startOfWeekDate: Date,
    dayIndex: number,
    hour: number,
    clickOffsetY: number,
    cellHeight: number,
    slotsPerWeek: BookingSlot[]
): Date {
    const hourStart = addDays(startOfWeekDate, dayIndex);
    hourStart.setHours(hour, 0, 0, 0);

    if (cellHeight <= 0) {
        return hourStart;
    }

    const clickMinutes = Math.min(
        59,
        Math.max(0, Math.floor((clickOffsetY / cellHeight) * 60))
    );
    const clickTime = addMinutes(hourStart, clickMinutes).getTime();

    const slotsInFrame = findSlotsForTimeFrame(
        startOfWeekDate,
        dayIndex,
        hour,
        slotsPerWeek
    );

    if (slotsInFrame.length === 0) {
        return addMinutes(hourStart, clickMinutes);
    }

    const hourStartMs = hourStart.getTime();
    const hourEndMs = addHours(hourStart, 1).getTime();

    const occupied = mergeOccupiedIntervals(
        slotsInFrame.map((slot) => ({
            start: Math.max(slot.from, hourStartMs),
            end: Math.min(
                addMinutes(slot.from, slot.duration).getTime(),
                hourEndMs
            )
        }))
    );

    let freeIntervalStart = hourStartMs;

    for (const block of occupied) {
        if (clickTime >= freeIntervalStart && clickTime < block.start) {
            return new Date(freeIntervalStart);
        }
        if (clickTime >= block.start && clickTime < block.end) {
            return new Date(block.end);
        }
        freeIntervalStart = Math.max(freeIntervalStart, block.end);
    }

    if (clickTime >= freeIntervalStart && clickTime < hourEndMs) {
        return new Date(freeIntervalStart);
    }

    return addMinutes(hourStart, clickMinutes);
}
